import type { ProjectPhase, RadioSnapshot } from "@agency/shared";
import { getStateCallbacks, type Room as SDKRoom } from "@colyseus/sdk";
import { type PlayerSnapshot, type ProjectSnapshot, store } from "../store/GameStore.js";

/* The only file that touches Colyseus schema callbacks. */

interface SchemaLike {
  [key: string]: unknown;
}

function snapshotPlayer(player: SchemaLike, sessionId: string): PlayerSnapshot {
  return {
    sessionId,
    nickname: String(player.nickname ?? ""),
    avatarId: String(player.avatarId ?? "ava_1"),
    role: String(player.role ?? "developer"),
    x: Number(player.x ?? 0),
    y: Number(player.y ?? 0),
    dir: Number(player.dir ?? 0),
    moving: Boolean(player.moving),
    activity: String(player.activity ?? "idle"),
    deskId: String(player.deskId ?? ""),
    exhaustion: Number(player.exhaustion ?? 0),
    burnedOut: Boolean(player.burnedOut),
    workSeconds: Number(player.workSeconds ?? 0),
    connected: Boolean(player.connected),
  };
}

function readProject(state: SchemaLike): ProjectSnapshot {
  const project = state.project as SchemaLike | undefined;
  return {
    id: Number(project?.id ?? 0),
    name: String(project?.name ?? ""),
    requiredUnits: Number(project?.requiredUnits ?? 0),
    completedUnits: Number(project?.completedUnits ?? 0),
  };
}

function readRadio(state: SchemaLike): RadioSnapshot {
  const radio = state.radio as SchemaLike | undefined;
  return {
    playing: Boolean(radio?.playing),
    stationId: String(radio?.stationId ?? ""),
    stationName: String(radio?.stationName ?? ""),
    streamUrl: String(radio?.streamUrl ?? ""),
    codec: String(radio?.codec ?? ""),
    bitrateKbps: Number(radio?.bitrateKbps ?? 0),
    logoUrl: String(radio?.logoUrl ?? ""),
    countryCode: String(radio?.countryCode ?? ""),
    tunedBy: String(radio?.tunedBy ?? ""),
    changedAt: Number(radio?.changedAt ?? 0),
  };
}

export function bindState(room: SDKRoom): void {
  const $ = getStateCallbacks(room) as unknown as (instance: unknown) => any;
  const state = room.state as unknown as SchemaLike;

  // A new room snapshot replaces everything from a previous connection; the
  // new state cannot deliver removals for players it never contained.
  for (const sessionId of [...store.players.keys()]) {
    store.players.delete(sessionId);
    store.emit("playerRemove", { sessionId });
  }
  store.desks.clear();

  store.sessionId = room.sessionId;
  store.phase = (state.phase as ProjectPhase) ?? "active";
  store.project = readProject(state);

  $(state).players.onAdd((player: SchemaLike, sessionId: string) => {
    const snap = snapshotPlayer(player, sessionId);
    store.players.set(sessionId, snap);
    store.emit("playerAdd", { sessionId, player: snap });

    $(player).onChange(() => {
      store.players.set(sessionId, snapshotPlayer(player, sessionId));
    });
  }, true);

  $(state).players.onRemove((_player: SchemaLike, sessionId: string) => {
    store.players.delete(sessionId);
    store.emit("playerRemove", { sessionId });
  });

  $(state).desks.onAdd((desk: SchemaLike, deskId: string) => {
    store.desks.set(deskId, String(desk.occupantId ?? ""));
    store.emit("deskChange", { deskId, occupantId: String(desk.occupantId ?? "") });
    $(desk).listen("occupantId", (value: string) => {
      store.desks.set(deskId, value);
      store.emit("deskChange", { deskId, occupantId: value });
    });
  }, true);

  $(state).listen(
    "phase",
    (value: ProjectPhase) => {
      store.phase = value;
      store.emit("phase", value);
    },
    true,
  );

  // The new snapshot wins outright, so a reconnect never replays an older station.
  const emitRadio = () => {
    store.radio = readRadio(state);
    store.emit("radio", store.radio);
  };
  emitRadio();
  if (state.radio) $(state.radio).onChange(emitRadio);

  for (const prop of ["completedUnits", "name", "id"] as const) {
    $(state.project).listen(
      prop,
      () => {
        store.project = readProject(state);
        store.emit("project", store.project);
      },
      true,
    );
  }
}
